import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PageLayout from '../components/PageLayout';

function Admin() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [assignUserId, setAssignUserId] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user is admin
    if (!user?.isAdmin) {
      navigate('/recipes');
      return;
    }

    fetchStats();
    fetchUsers();
  }, [isAuthenticated, user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMealPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/meal-plans`);
      setMealPlans(response.data);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`/api/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'recipes') {
      fetchRecipes();
    } else if (activeTab === 'meal-plans') {
      fetchMealPlans();
    } else {
      setLoading(false);
    }
  }, [activeTab]);

  const handleAssignRecipes = async () => {
    if (!assignUserId) {
      alert('Please select a user');
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post(`/api/admin/assign-recipes`, {
        userId: assignUserId
      });
      alert(response.data.message);
      fetchStats();
      if (activeTab === 'recipes') {
        fetchRecipes();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to assign recipes');
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignMealPlans = async () => {
    if (!assignUserId) {
      alert('Please select a user');
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post(`/api/admin/assign-meal-plans`, {
        userId: assignUserId
      });
      alert(response.data.message);
      fetchStats();
      if (activeTab === 'meal-plans') {
        fetchMealPlans();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to assign meal plans');
    } finally {
      setAssigning(false);
    }
  };

  const toggleAdminStatus = async (userId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'remove' : 'grant'} admin access for this user?`)) {
      return;
    }

    try {
      await axios.put(`/api/admin/users/${userId}/admin`, {
        isAdmin: !currentStatus
      });
      fetchUsers();
      // If user updated their own status, refresh auth
      if (userId === user.id) {
        window.location.reload();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update admin status');
    }
  };

  if (!user?.isAdmin) {
    return null;
  }

  return (
    <>
      <div 
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/form-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
          minHeight: '100vh',
          height: '100%'
        }}
      />
      <PageLayout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Admin Panel</h1>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-6 border-b">
              <button
                onClick={() => setActiveTab('stats')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  activeTab === 'stats'
                    ? 'border-b-2 border-amber-600 text-amber-600'
                    : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                Statistics
              </button>
              <button
                onClick={() => setActiveTab('recipes')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  activeTab === 'recipes'
                    ? 'border-b-2 border-amber-600 text-amber-600'
                    : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                All Recipes
              </button>
              <button
                onClick={() => setActiveTab('meal-plans')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  activeTab === 'meal-plans'
                    ? 'border-b-2 border-amber-600 text-amber-600'
                    : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                All Meal Plans
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  activeTab === 'users'
                    ? 'border-b-2 border-amber-600 text-amber-600'
                    : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                Users
              </button>
            </div>

            {/* Stats Tab */}
            {activeTab === 'stats' && stats && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-white/80 p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700">Total Users</h3>
                    <p className="text-3xl font-bold text-amber-600">{stats.totalUsers}</p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700">Total Recipes</h3>
                    <p className="text-3xl font-bold text-amber-600">{stats.totalRecipes}</p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700">Total Meal Plans</h3>
                    <p className="text-3xl font-bold text-amber-600">{stats.totalMealPlans}</p>
                  </div>
                </div>

                {stats.orphanedData && (
                  <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">⚠️ Orphaned Data Detected</h3>
                    <p className="mb-2">
                      There are {stats.recipesWithoutUser} recipes and {stats.mealPlansWithoutUser} meal plans 
                      without a user assigned (created before authentication was implemented).
                    </p>
                    <div className="mt-4 space-y-2">
                      <div className="flex gap-2 items-center">
                        <select
                          value={assignUserId}
                          onChange={(e) => setAssignUserId(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">Select a user...</option>
                          {users.map((u) => (
                            <option key={u._id} value={u._id}>
                              {u.username} ({u.email})
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={handleAssignRecipes}
                          disabled={assigning || !assignUserId}
                          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-400"
                        >
                          {assigning ? 'Assigning...' : 'Assign Recipes'}
                        </button>
                        <button
                          onClick={handleAssignMealPlans}
                          disabled={assigning || !assignUserId}
                          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-400"
                        >
                          {assigning ? 'Assigning...' : 'Assign Meal Plans'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recipes Tab */}
            {activeTab === 'recipes' && (
              <div>
                {loading ? (
                  <p>Loading recipes...</p>
                ) : (
                  <div className="space-y-2">
                    <p className="mb-4 text-gray-600">Total: {recipes.length} recipes</p>
                    {recipes.map((recipe) => (
                      <div key={recipe._id} className="bg-white/80 p-4 rounded-lg shadow">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-lg">{recipe.title}</h3>
                            <p className="text-sm text-gray-600">
                              Created: {new Date(recipe.createdAt).toLocaleDateString()}
                              {recipe.userId ? (
                                typeof recipe.userId === 'object' && recipe.userId.username
                                  ? ` | User: ${recipe.userId.username} (${recipe.userId.email})`
                                  : ` | User ID: ${recipe.userId}`
                              ) : (
                                ' | ⚠️ No user assigned'
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Meal Plans Tab */}
            {activeTab === 'meal-plans' && (
              <div>
                {loading ? (
                  <p>Loading meal plans...</p>
                ) : (
                  <div className="space-y-2">
                    <p className="mb-4 text-gray-600">Total: {mealPlans.length} meal plans</p>
                    {mealPlans.map((mealPlan) => (
                      <div key={mealPlan._id} className="bg-white/80 p-4 rounded-lg shadow">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-lg">
                              {mealPlan.recipeId?.title || 'Recipe not found'}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Date: {new Date(mealPlan.date).toLocaleDateString()} | 
                              Type: {mealPlan.mealType}
                              {mealPlan.userId ? (
                                ` | User: ${mealPlan.userId.username || mealPlan.userId.email}`
                              ) : (
                                ' | ⚠️ No user assigned'
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <div className="space-y-2">
                  {users.map((u) => (
                    <div key={u._id} className="bg-white/80 p-4 rounded-lg shadow flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {u.username} {u.isAdmin && <span className="text-amber-600">(Admin)</span>}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {u.email} | Joined: {new Date(u.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => toggleAdminStatus(u._id, u.isAdmin)}
                        className={`px-4 py-2 rounded-lg font-semibold ${
                          u.isAdmin
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-amber-600 text-white hover:bg-amber-700'
                        }`}
                      >
                        {u.isAdmin ? 'Remove Admin' : 'Make Admin'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </PageLayout>
    </>
  );
}

export default Admin;

